/* eslint-disable no-nested-ternary */
import { isEmpty } from 'ramda';
import React from 'react';
import { showAlert, useAuth, User } from '../../../../context/authContext';
import {
  onItemDelete,
  storePreferenceFormAction,
  StorePreferences,
  Tax,
} from '../../../../context/storeContext';
import env from '../../../../utils/env';
import { formatErrors } from '../../../../utils/request';
import Button from '../../../Button/Button';
import Icon from '../../../Icon/Icon';
import DeletePrompt from '../../../Modal/DeletePrompt/DeletePrompt';
import TaxAdd from '../../../Modal/TaxAdd/TaxAdd';

type TaxesTabProps = {
  user: User;
  storePref: StorePreferences;
  showAddTaxModal: boolean;
  onCloseAddTaxModal: (value: boolean) => void;
};

function TaxesTab({
  user,
  storePref,
  showAddTaxModal,
  onCloseAddTaxModal,
}: TaxesTabProps) {
  const [, dispatch] = useAuth();
  const [taxes, setTaxes] = React.useState<Tax[]>([]);
  const [selectedTax, setSelectedTax] = React.useState<Tax>();
  const [deleteID, setDeleteID] = React.useState<number>();
  const [showAddSchemeModal, setShowAddSchemeModal] = React.useState(false);

  React.useEffect(() => {
    if (storePref.taxes) {
      setTaxes(storePref.taxes);
    }
  }, [storePref.taxes]);

  const handleTaxAdd = async (newTax: {
    name: string;
    rate: number;
    description: string;
  }) => {
    if (!user.defaultStore) {
      return;
    }

    try {
      const { tax: getTax } = (await storePreferenceFormAction(
        `/tax?store=${user.defaultStore.id}`,
        !selectedTax ? 'post' : 'put',
        {
          ...newTax,
          storeId: user.defaultStore.id,
          ...(selectedTax && { taxId: selectedTax.id }),
        },
      )) as any;

      console.log('storePreferenceFormAction', getTax);

      if (storePref && getTax) {
        dispatch({
          type: 'SET_STORE_PREFERENCES',
          payload: {
            ...storePref,
            taxes: selectedTax
              ? storePref.taxes.map(fndTax => {
                  if (fndTax.id === selectedTax.id) {
                    return getTax;
                  }
                  return fndTax;
                })
              : storePref.taxes.length > 0
              ? [getTax, ...storePref.taxes]
              : [getTax],
          },
        });
      }

      onCloseAddTaxModal(false);

      setSelectedTax(undefined);

      setShowAddSchemeModal(false);

      window.location.reload();
    } catch (error) {
      const { message } = formatErrors(error);

      showAlert(dispatch, {
        show: true,
        message,
        type: 'ERROR',
      });
    }
  };

  if (env('POS_ENV') === 'production') {
    return <div className="" />;
  }

  return (
    <>
      <div className="flex flex-row items-start mt-10 space-x-10">
        <div className="w-[40%]">
          <div className="bg-white w-full rounded-md border border-[rgba(143,115,25,0.4)]">
            <table className="border-collapse table-auto w-full h-full bg-white table-striped rounded-md relative">
              <thead>
                <tr className="text-left !bg-[#FFFBDF] rounded-tr-md border-b !border-[rgba(143,115,25,0.4)] h-[44.59px]">
                  <th className="!text-xbase font-semibold !border-[rgba(143,115,25,0.4)]">
                    Name
                  </th>
                  <th className="!text-xbase font-semibold !border-[rgba(143,115,25,0.4)] w-[20%]">
                    Rate
                  </th>
                  <th scope="col" className="w-[20%]">
                    &nbsp;
                  </th>
                </tr>
              </thead>
              <tbody className="relative">
                {taxes.filter(
                  fndTax => !fndTax.groupings || isEmpty(fndTax.groupings),
                ).length === 0 ? (
                  <tr className="w-full h-full bg-[#f2f2f2] bg-opacity-75 transition-opacity">
                    <td
                      colSpan={5}
                      rowSpan={4}
                      className="!border-none text-center"
                    >
                      <span className="text-pos-black test-sm font-medium">
                        No taxes found!
                      </span>
                    </td>
                  </tr>
                ) : (
                  taxes
                    .filter(
                      fndTax => !fndTax.groupings || isEmpty(fndTax.groupings),
                    )
                    .map((fndTax, index) => (
                      <tr key={fndTax.id}>
                        <td>{fndTax.name}</td>
                        <td>
                          {fndTax.rate}
                          <span className="text-xbase">%</span>
                        </td>
                        <td>
                          <div className="flex flex-row items-center justify-end space-x-8">
                            <button
                              onClick={() => setSelectedTax(fndTax)}
                              type="button"
                              className="w-[16px] h-[16px] transform hover:text-purple-500 hover:scale-110"
                            >
                              <Icon
                                id="iconTableEdit"
                                className="w-[16px] h-[16px] fill-pos-gray"
                              />
                            </button>
                            <button
                              onClick={() => setDeleteID(fndTax.id)}
                              type="button"
                              className="w-[16px] h-[16px] transform hover:text-purple-500 hover:scale-110"
                            >
                              <Icon
                                id="iconTableDelete"
                                className="w-[16px] h-[16px] fill-pos-gray"
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="w-full">
          <div className="flex flex-row mb-4 items-end justify-between space-x-10">
            <div>
              <h3 className="text-pos-black text-lg font-semibold leading-none">
                Tax Schemes
              </h3>
              <p className="text-pos-gray font-light mt-1">
                Combine multiple taxes into a scheme to account for different
                types of taxes on sales.
              </p>
            </div>
            <Button
              label="Add new Scheme"
              onClick={() => setShowAddSchemeModal(true)}
              disabled={false}
              loading={false}
              type="button"
              className="!bg-[#f6f8fa] min-w-[150px] h-[45px] items-center justify-center 
                  !text-pos-black text-xbase border border-[rgba(27,31,36,0.15)] shrink-0 
                  shadow-[0_1px_0_rgba(27,31,36,0.04),inset_0_1px_0_rgba(255,255,255,0.25)] font-medium"
            />
          </div>

          <div className="bg-white w-full rounded-md border border-[rgba(216,222,228,0.8)]">
            <table className="border-collapse table-auto w-full h-full bg-white table-striped rounded-md relative">
              <thead>
                <tr className="text-left !bg-pos-header-bg rounded-tr-md border-b !border-[rgba(216,222,228,0.8)] h-[44.59px]">
                  <th className="!text-xbase font-semibold !border-[rgba(216,222,228,0.8)]">
                    Scheme Name
                  </th>
                  <th className="!text-xbase font-semibold !border-[rgba(216,222,228,0.8)]">
                    Taxes
                  </th>
                  <th className="!text-xbase font-semibold !border-[rgba(216,222,228,0.8)] w-[10  %]">
                    Rate
                  </th>
                  <th scope="col" className="w-[10%]">
                    &nbsp;
                  </th>
                </tr>
              </thead>
              <tbody className="relative">
                {taxes.filter(
                  fndTax =>
                    fndTax.groupings &&
                    Object.keys(fndTax.groupings).length > 0,
                ).length === 0 ? (
                  <tr className="w-full h-full bg-[#f2f2f2] bg-opacity-75 transition-opacity">
                    <td
                      colSpan={5}
                      rowSpan={4}
                      className="!border-none text-center"
                    >
                      <span className="text-pos-black test-sm font-medium">
                        No tax scheme added!
                      </span>
                    </td>
                  </tr>
                ) : (
                  taxes
                    .filter(
                      fndTax =>
                        fndTax.groupings &&
                        Object.keys(fndTax.groupings).length > 0,
                    )
                    .map((fndTaxScheme, index) => (
                      <React.Fragment key={fndTaxScheme.id}>
                        <tr>
                          <td>{fndTaxScheme.name}</td>
                          <td className="font-medium text-pos-black">
                            {fndTaxScheme?.groupings
                              ? fndTaxScheme.groupings.length
                              : 0}{' '}
                            Taxes
                          </td>
                          <td className="font-medium text-pos-black">
                            {fndTaxScheme.rate}
                            <span className="text-xbase">%</span>
                          </td>
                          <td>
                            <div className="flex flex-row items-center justify-end space-x-8">
                              <button
                                onClick={() => {
                                  setSelectedTax(fndTaxScheme);
                                  // setShowAddSchemeModal(true);
                                }}
                                type="button"
                                className="w-[16px] h-[16px] transform hover:text-purple-500 hover:scale-110"
                              >
                                <Icon
                                  id="iconTableEdit"
                                  className="w-[16px] h-[16px] fill-pos-gray"
                                />
                              </button>
                              <button
                                onClick={() => setDeleteID(fndTaxScheme.id)}
                                type="button"
                                className="w-[16px] h-[16px] transform hover:text-purple-500 hover:scale-110"
                              >
                                <Icon
                                  id="iconTableDelete"
                                  className="w-[16px] h-[16px] fill-pos-gray"
                                />
                              </button>
                            </div>
                          </td>
                        </tr>

                        {fndTaxScheme?.groupings
                          ? fndTaxScheme?.groupings.map(
                              (fndGroupTax, fndIndex) => {
                                const findTax = taxes.find(
                                  fndTax => fndTax.id === fndGroupTax.taxId,
                                );

                                if (!findTax) {
                                  return null;
                                }

                                return (
                                  <tr
                                    key={`${fndGroupTax.taxId}${fndTaxScheme.name}`}
                                    className={`${
                                      (fndTaxScheme.groupings?.length || 0) -
                                        1 ===
                                      fndIndex
                                        ? 'border-b border-[rgba(216,222,228,0.8)]'
                                        : ''
                                    } last:!border-0`}
                                  >
                                    <td />
                                    <td>{findTax.name}</td>
                                    <td>
                                      {findTax.rate}
                                      <span className="text-xbase">%</span>
                                    </td>
                                    <td />
                                  </tr>
                                );
                              },
                            )
                          : null}
                      </React.Fragment>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {(showAddTaxModal || selectedTax || showAddSchemeModal) && (
        <TaxAdd
          onAddTax={handleTaxAdd}
          taxes={taxes.filter(
            fndTax => !fndTax.groupings || isEmpty(fndTax.groupings),
          )}
          tax={selectedTax}
          addingTaxScheme={showAddSchemeModal}
          currencySymbol={user?.defaultStore?.country?.currencySymbol || '₵'}
          onCloseModal={() => {
            onCloseAddTaxModal(false);
            setSelectedTax(undefined);
            setShowAddSchemeModal(false);
          }}
        />
      )}

      {deleteID && user.defaultStore && (
        <DeletePrompt
          showArchive={false}
          onCloseModal={() => setDeleteID(undefined)}
          onRemoveItem={() => {
            dispatch({
              type: 'SET_STORE_PREFERENCES',
              payload: {
                ...storePref,
                taxes: storePref.taxes.filter(fndTax => fndTax.id !== deleteID),
              },
            });
          }}
          deleteCb={async () => {
            onItemDelete(`/tax?store=${user.defaultStore.id}`, {
              taxId: deleteID,
            });
          }}
        />
      )}
    </>
  );
}

export default TaxesTab;
